import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader, SyncButton, AdminEmptyState, ResponsiveTableWrap } from "@/components/admin/crud-helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Copy, Pencil, Trash2, Users, Boxes, BarChart3, Settings2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/branches")({
  component: BranchesPage,
});

type BranchType = "company" | "franchise";
type BranchStatus = "active" | "paused" | "closed";

type Branch = {
  id: string;
  name: string;
  code: string;
  type: BranchType;
  status: BranchStatus;
  region: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  manager_id: string | null;
  commission_rate: number;
  contract_start: string | null;
  contract_end: string | null;
  notes: string | null;
  created_at: string;
};

function BranchesPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Branch | null>(null);
  const [drill, setDrill] = useState<Branch | null>(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | BranchType>("all");
  const [statusFilter, setStatusFilter] = useState<"all" | BranchStatus>("all");

  const branches = useQuery({
    queryKey: ["branches"],
    queryFn: async () => {
      const { data, error } = await supabase.from("branches").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Branch[];
    },
  });

  const orderStats = useQuery({
    queryKey: ["branch_order_stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("branch_id, total_cents, status")
        .not("branch_id", "is", null)
        .limit(5000);
      if (error) throw error;
      const map = new Map<string, { orders: number; revenue: number }>();
      for (const o of data ?? []) {
        if (!o.branch_id) continue;
        const cur = map.get(o.branch_id) ?? { orders: 0, revenue: 0 };
        if (o.status === "paid" || o.status === "fulfilled") {
          cur.orders += 1;
          cur.revenue += (o.total_cents ?? 0) / 100;
        }
        map.set(o.branch_id, cur);
      }
      return map;
    },
  });

  const remove = async (id: string) => {
    if (!confirm("Delete this branch?")) return;
    const { error } = await supabase.from("branches").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["branches"] }); }
  };

  const duplicate = async (b: Branch) => {
    const existing = new Set((branches.data ?? []).map((x) => x.code));
    let code = `${b.code}-COPY`;
    let i = 2;
    while (existing.has(code)) code = `${b.code}-COPY${i++}`;
    const { error } = await supabase.from("branches").insert({
      name: `${b.name} (Copy)`,
      code,
      type: b.type,
      status: "paused" as BranchStatus,
      region: b.region,
      address: b.address,
      phone: b.phone,
      email: b.email,
      manager_id: null,
      commission_rate: b.commission_rate,
      contract_start: null,
      contract_end: null,
      notes: b.notes,
    });
    if (error) toast.error(error.message);
    else { toast.success("Branch duplicated (paused)"); qc.invalidateQueries({ queryKey: ["branches"] }); }
  };

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (branches.data ?? []).filter((b) => {
      if (typeFilter !== "all" && b.type !== typeFilter) return false;
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (!q) return true;
      return [b.name, b.code, b.region, b.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [branches.data, search, typeFilter, statusFilter]);

  const totals = useMemo(() => {
    const arr = branches.data ?? [];
    return {
      total: arr.length,
      company: arr.filter(b => b.type === "company").length,
      franchise: arr.filter(b => b.type === "franchise").length,
      active: arr.filter(b => b.status === "active").length,
    };
  }, [branches.data]);

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Branches & franchises"
        description="Manage company branches and franchise locations."
        onNew={() => { setEditing(null); setOpen(true); }}
        newLabel="New branch"
        actions={<SyncButton onSync={() => branches.refetch()} loading={branches.isFetching} />}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search name, code, region, email…"
        count={rows.length}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Total" value={totals.total} />
        <Kpi label="Company" value={totals.company} />
        <Kpi label="Franchise" value={totals.franchise} />
        <Kpi label="Active" value={totals.active} />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {(["all", "company", "franchise"] as const).map((t) => (
          <Button key={t} size="sm" variant={typeFilter === t ? "default" : "outline"} onClick={() => setTypeFilter(t)}>
            {t === "all" ? "All types" : t}
          </Button>
        ))}
        <span className="mx-1 hidden h-6 w-px self-center bg-border sm:block" />
        {(["all", "active", "paused", "closed"] as const).map((s) => (
          <Button key={s} size="sm" variant={statusFilter === s ? "default" : "outline"} onClick={() => setStatusFilter(s)}>
            {s === "all" ? "All statuses" : s}
          </Button>
        ))}
      </div>

      {!branches.isLoading && rows.length === 0 ? (
        <AdminEmptyState title={search || typeFilter !== "all" || statusFilter !== "all" ? "No branches match" : "No branches yet"} />
      ) : (
        <div className="rounded-lg border border-border bg-card">
          <ResponsiveTableWrap>
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-xs">
                <tr>
                  <th className="px-4 py-2 text-left">Branch</th>
                  <th className="hidden px-4 py-2 text-left md:table-cell">Type</th>
                  <th className="hidden px-4 py-2 text-left lg:table-cell">Region</th>
                  <th className="hidden px-4 py-2 text-right md:table-cell">Commission</th>
                  <th className="hidden px-4 py-2 text-right sm:table-cell">Orders</th>
                  <th className="px-4 py-2 text-right">Revenue</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {branches.isLoading && <tr><td colSpan={8} className="px-4 py-6 text-muted-foreground">Loading…</td></tr>}
                {rows.map(b => {
                  const stat = orderStats.data?.get(b.id) ?? { orders: 0, revenue: 0 };
                  return (
                    <tr key={b.id} className="border-t border-border hover:bg-secondary/40">
                      <td className="px-4 py-2">
                        <div className="font-medium">{b.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {b.code}
                          <span className="md:hidden"> · {b.type} · {Number(b.commission_rate).toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="hidden px-4 py-2 md:table-cell"><Badge variant="outline">{b.type}</Badge></td>
                      <td className="hidden px-4 py-2 text-muted-foreground lg:table-cell">{b.region ?? "—"}</td>
                      <td className="hidden px-4 py-2 text-right md:table-cell">{Number(b.commission_rate).toFixed(2)}%</td>
                      <td className="hidden px-4 py-2 text-right sm:table-cell">{stat.orders}</td>
                      <td className="px-4 py-2 text-right">${stat.revenue.toFixed(2)}</td>
                      <td className="px-4 py-2">
                        <Badge variant={b.status === "active" ? "secondary" : b.status === "closed" ? "destructive" : "outline"}>
                          {b.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Manage" onClick={() => setDrill(b)}>
                            <Settings2 className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" onClick={() => { setEditing(b); setOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Duplicate" onClick={() => duplicate(b)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Delete" onClick={() => remove(b.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ResponsiveTableWrap>
        </div>
      )}

      <BranchDialog
        open={open}
        onOpenChange={setOpen}
        branch={editing}
        onSaved={() => qc.invalidateQueries({ queryKey: ["branches"] })}
      />

      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{drill?.name}</DialogTitle>
          </DialogHeader>
          {drill && <BranchDrilldown branch={drill} stats={orderStats.data?.get(drill.id)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-1 text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function BranchDialog({
  open, onOpenChange, branch, onSaved,
}: { open: boolean; onOpenChange: (v: boolean) => void; branch: Branch | null; onSaved: () => void }) {
  const empty = {
    name: "", code: "", type: "company" as BranchType, status: "active" as BranchStatus,
    region: "", address: "", phone: "", email: "", manager_id: "",
    commission_rate: 0, contract_start: "", contract_end: "", notes: "", map_embed_url: "",
  };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (!open) return;
    setForm(branch ? {
      name: branch.name, code: branch.code, type: branch.type, status: branch.status,
      region: branch.region ?? "", address: branch.address ?? "", phone: branch.phone ?? "",
      email: branch.email ?? "", manager_id: branch.manager_id ?? "",
      commission_rate: Number(branch.commission_rate), contract_start: branch.contract_start ?? "",
      contract_end: branch.contract_end ?? "", notes: branch.notes ?? "",
      map_embed_url: (branch as any).map_embed_url ?? "",
    } : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branch]);

  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error("Name and code required"); return; }
    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      type: form.type,
      status: form.status,
      region: form.region || null,
      address: form.address || null,
      phone: form.phone || null,
      email: form.email || null,
      manager_id: form.manager_id || null,
      commission_rate: Number(form.commission_rate) || 0,
      contract_start: form.contract_start || null,
      contract_end: form.contract_end || null,
      notes: form.notes || null,
      map_embed_url: form.map_embed_url || null,
    };
    const op = branch
      ? supabase.from("branches").update(payload).eq("id", branch.id)
      : supabase.from("branches").insert(payload);
    const { error } = await op;
    if (error) { toast.error(error.message); return; }
    toast.success("Saved");
    onSaved();
    onOpenChange(false);
  };

  const set = <K extends keyof typeof form>(k: K, v: typeof form[K]) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{branch ? "Edit branch" : "New branch"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="grid gap-2">
            <Label>Name</Label>
            <Input value={form.name} onChange={e => set("name", e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Code</Label>
            <Input value={form.code} onChange={e => set("code", e.target.value)} placeholder="NYC-01" />
          </div>
          <div className="grid gap-2">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => set("type", v as BranchType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="franchise">Franchise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v as BranchStatus)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Region</Label>
            <Input value={form.region} onChange={e => set("region", e.target.value)} placeholder="North America" />
          </div>
          <div className="grid gap-2">
            <Label>Commission rate (%)</Label>
            <Input type="number" step="0.1" value={form.commission_rate} onChange={e => set("commission_rate", Number(e.target.value))} />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label>Address</Label>
            <Input value={form.address} onChange={e => set("address", e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={e => set("phone", e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={e => set("email", e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Manager (user ID)</Label>
            <Input value={form.manager_id} onChange={e => set("manager_id", e.target.value)} placeholder="uuid" />
          </div>
          <div className="grid gap-2">
            <Label>Contract start</Label>
            <Input type="date" value={form.contract_start} onChange={e => set("contract_start", e.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>Contract end</Label>
            <Input type="date" value={form.contract_end} onChange={e => set("contract_end", e.target.value)} />
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label>Google Maps embed URL</Label>
            <Input
              value={form.map_embed_url}
              onChange={e => set("map_embed_url", e.target.value)}
              placeholder="https://www.google.com/maps/embed?pb=..."
            />
            <p className="text-xs text-muted-foreground">Paste only the <code>src</code> URL from the Google Maps embed iframe.</p>
          </div>
          <div className="grid gap-2 md:col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={e => set("notes", e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BranchDrilldown({ branch, stats }: { branch: Branch; stats?: { orders: number; revenue: number } }) {
  const qc = useQueryClient();
  const staff = useQuery({
    queryKey: ["branch_staff", branch.id],
    queryFn: async () => {
      const { data, error } = await supabase.from("branch_staff").select("*").eq("branch_id", branch.id);
      if (error) throw error;
      return data ?? [];
    },
  });
  const inventory = useQuery({
    queryKey: ["branch_inventory", branch.id],
    queryFn: async () => {
      const { data, error } = await supabase.from("branch_inventory").select("*").eq("branch_id", branch.id);
      if (error) throw error;
      return data ?? [];
    },
  });

  const [staffUser, setStaffUser] = useState("");
  const [staffRole, setStaffRole] = useState("staff");
  const addStaff = async () => {
    if (!staffUser.trim()) return;
    const { error } = await supabase.from("branch_staff").insert({ branch_id: branch.id, user_id: staffUser.trim(), role: staffRole });
    if (error) toast.error(error.message);
    else { toast.success("Added"); setStaffUser(""); qc.invalidateQueries({ queryKey: ["branch_staff", branch.id] }); }
  };
  const removeStaff = async (id: string) => {
    const { error } = await supabase.from("branch_staff").delete().eq("id", id);
    if (error) toast.error(error.message);
    else qc.invalidateQueries({ queryKey: ["branch_staff", branch.id] });
  };

  const [invVar, setInvVar] = useState("");
  const [invQty, setInvQty] = useState(0);
  const addInv = async () => {
    if (!invVar.trim()) return;
    const { error } = await supabase.from("branch_inventory").upsert({
      branch_id: branch.id, variant_id: invVar.trim(), qty: invQty,
    }, { onConflict: "branch_id,variant_id" });
    if (error) toast.error(error.message);
    else { toast.success("Saved"); setInvVar(""); setInvQty(0); qc.invalidateQueries({ queryKey: ["branch_inventory", branch.id] }); }
  };

  return (
    <Tabs defaultValue="perf">
      <PillTabsList search>
        <PillTabsTrigger value="perf"><BarChart3 className="mr-2 h-4 w-4" /> Performance</PillTabsTrigger>
        <PillTabsTrigger value="staff"><Users className="mr-2 h-4 w-4" /> Staff</PillTabsTrigger>
        <PillTabsTrigger value="inv"><Boxes className="mr-2 h-4 w-4" /> Inventory</PillTabsTrigger>
      </PillTabsList>

      <TabsContent value="perf" className="pt-4">
        <div className="grid grid-cols-3 gap-3">
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Orders</div><div className="text-2xl font-semibold">{stats?.orders ?? 0}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Revenue</div><div className="text-2xl font-semibold">${(stats?.revenue ?? 0).toFixed(2)}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs uppercase text-muted-foreground">Commission earned</div><div className="text-2xl font-semibold">${((stats?.revenue ?? 0) * Number(branch.commission_rate) / 100).toFixed(2)}</div></CardContent></Card>
        </div>
        <div className="mt-4 text-sm text-muted-foreground">
          Contract: {branch.contract_start ?? "—"} → {branch.contract_end ?? "—"} · Commission {Number(branch.commission_rate).toFixed(2)}%
        </div>
      </TabsContent>

      <TabsContent value="staff" className="pt-4 space-y-4">
        <div className="flex gap-2">
          <Input placeholder="User ID" value={staffUser} onChange={e => setStaffUser(e.target.value)} />
          <Input placeholder="Role" className="w-40" value={staffRole} onChange={e => setStaffRole(e.target.value)} />
          <Button onClick={addStaff}>Add</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs"><tr><th className="px-3 py-2 text-left">User</th><th className="px-3 py-2 text-left">Role</th><th className="px-3 py-2"></th></tr></thead>
          <tbody>
            {(staff.data ?? []).length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-muted-foreground">No staff assigned.</td></tr>}
            {(staff.data ?? []).map(s => (
              <tr key={s.id} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">{s.user_id.slice(0, 8)}…</td>
                <td className="px-3 py-2">{s.role}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="icon" variant="ghost" onClick={() => removeStaff(s.id)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabsContent>

      <TabsContent value="inv" className="pt-4 space-y-4">
        <div className="flex gap-2">
          <Input placeholder="Variant ID" value={invVar} onChange={e => setInvVar(e.target.value)} />
          <Input type="number" placeholder="Qty" className="w-32" value={invQty} onChange={e => setInvQty(Number(e.target.value))} />
          <Button onClick={addInv}>Set</Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs"><tr><th className="px-3 py-2 text-left">Variant</th><th className="px-3 py-2 text-left">Qty</th><th className="px-3 py-2 text-left">Low threshold</th></tr></thead>
          <tbody>
            {(inventory.data ?? []).length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-muted-foreground">No inventory yet.</td></tr>}
            {(inventory.data ?? []).map(i => (
              <tr key={i.id} className="border-t border-border">
                <td className="px-3 py-2 font-mono text-xs">{i.variant_id.slice(0, 8)}…</td>
                <td className="px-3 py-2">
                  {i.qty}
                  {i.qty <= i.low_stock_threshold && <Badge variant="destructive" className="ml-2">Low</Badge>}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{i.low_stock_threshold}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabsContent>
    </Tabs>
  );
}
